import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { DocumentStatus, type PublicDocument } from "@vedaunion/shared";
import { useDocuments, useDocumentTags } from "../../lib/documents";
import { useCan } from "../../lib/auth";

/**
 * Authenticated documents index. Three sub-tabs: Mine / All / Published.
 * Filter chips: status, type tag, deity tag, pinned.
 */

type View = "mine" | "all" | "library";

const STATUS_OPTIONS = DocumentStatus.options;

export default function DocumentsList() {
  const canCreate = useCan("documents.create");
  const canEditAny = useCan("documents.edit_any");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const availableViews: View[] = useMemo(() => {
    const v: View[] = [];
    if (canCreate) v.push("mine");
    if (canEditAny) v.push("all");
    v.push("library");
    return v;
  }, [canCreate, canEditAny]);

  const requestedView = params.get("view") as View | null;
  const view: View =
    requestedView && availableViews.includes(requestedView) ? requestedView : availableViews[0]!;

  const status = params.get("status") ?? "";
  const tagFilter = params.get("tag") ?? "";
  const pinned = params.get("pinned") === "1";

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const tagsQ = useDocumentTags();
  const tags = tagsQ.data?.tags ?? [];

  const apiParams = useMemo(() => {
    const base = {
      limit: 100,
      ...(tagFilter ? { tag: tagFilter } : {}),
      ...(pinned ? { pinned: true } : {}),
    } as const;
    if (view === "library") return base;
    return {
      ...base,
      ...(view === "mine" ? { mine: true } : { all: true }),
      ...(status ? { status } : {}),
    } as const;
  }, [view, status, tagFilter, pinned]);

  const docsQ = useDocuments(apiParams);
  const documents = docsQ.data?.documents ?? [];
  const isLoading = docsQ.isLoading;

  const showAuthor = view !== "mine";
  const isEditableView = view !== "library";

  return (
    <div className="container-edit">
      <header className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="page-eyebrow mb-1.5">Documents</p>
          <h1 className="page-title">Mantras, sūktas, sādhanas.</h1>
          <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
            A document holds the introduction and the materials — PDFs in
            different scripts, audio per anuvāka, the YouTube class playlist,
            study links. The body is the framing; the resources are the work.
          </p>
        </div>
        {canCreate && (
          <Link to="/dashboard/documents/new" className="btn-app">
            New document
          </Link>
        )}
      </header>

      <nav
        className="flex flex-wrap gap-x-4 gap-y-2 border-b border-[var(--color-rule)] mb-5"
        aria-label="Document views"
      >
        {availableViews.map((v) => (
          <button
            key={v}
            type="button"
            className={`subtab ${view === v ? "is-active" : ""}`}
            onClick={() => setParam("view", v === availableViews[0] ? null : v)}
          >
            {viewLabel(v)}
          </button>
        ))}
      </nav>

      <div className="flex flex-wrap items-center gap-2 mb-7">
        {isEditableView && (
          <FilterChip
            label="Status"
            value={status}
            options={STATUS_OPTIONS as readonly string[]}
            onChange={(v) => setParam("status", v)}
          />
        )}
        <FilterChip
          label="Tag"
          value={tagFilter}
          options={tags.map((t) => `${t.namespace}:${t.slug}`)}
          formatLabel={(v) => {
            const t = tags.find((tag) => `${tag.namespace}:${tag.slug}` === v);
            return t ? `${t.namespace} · ${t.label}` : v;
          }}
          onChange={(v) => setParam("tag", v)}
        />
        <button
          type="button"
          className={`chip ${pinned ? "is-active" : ""}`}
          onClick={() => setParam("pinned", pinned ? null : "1")}
        >
          Pinned only
        </button>
        {(status || tagFilter || pinned) && (
          <button
            type="button"
            className="chip chip-clear"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("status");
              next.delete("tag");
              next.delete("pinned");
              setParams(next, { replace: true });
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-[var(--color-ink-mute)]">Loading…</p>
      ) : documents.length === 0 ? (
        <EmptyState view={view} canCreate={canCreate} />
      ) : (
        <ul className="flex flex-col">
          {documents.map((d) => (
            <DocumentRow
              key={d.id}
              d={d}
              showAuthor={showAuthor}
              showEdit={isEditableView}
              onOpen={() =>
                navigate(isEditableView ? `/dashboard/documents/${d.slug}/edit` : `/library/${d.slug}`)
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function viewLabel(v: View): string {
  if (v === "mine") return "My documents";
  if (v === "all") return "All documents";
  return "Library";
}

function FilterChip({
  label,
  value,
  options,
  formatLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  formatLabel?: (v: string) => string;
  onChange: (v: string) => void;
}) {
  return (
    <label className={`chip chip-select ${value ? "is-active" : ""}`}>
      <span className="chip-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">all</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {formatLabel ? formatLabel(o) : o}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyState({ view, canCreate }: { view: View; canCreate: boolean }) {
  const title =
    view === "library"
      ? "Nothing published yet."
      : view === "all"
        ? "No documents match these filters."
        : "Nothing of yours yet.";
  const body =
    view === "library"
      ? "Published documents surface here and on the public Library."
      : view === "all"
        ? "Try clearing the filters or switch to a different view."
        : "Begin a new document — start with a template or a blank canvas.";
  return (
    <div className="veda-empty">
      <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-soft)] mb-1.5">{title}</p>
      <p className="text-[0.95rem] text-[var(--color-ink-soft)] max-w-[52ch] mx-auto leading-[1.55]">
        {body}
      </p>
      {canCreate && view !== "library" && (
        <Link to="/dashboard/documents/new" className="btn-app mt-5 inline-flex">
          Begin a new document
        </Link>
      )}
    </div>
  );
}

function DocumentRow({
  d,
  showAuthor,
  showEdit,
  onOpen,
}: {
  d: PublicDocument;
  showAuthor: boolean;
  showEdit: boolean;
  onOpen: () => void;
}) {
  const stamp = d.publishedAt ?? d.updatedAt;
  return (
    <li className="grid grid-cols-1 sm:grid-cols-[180px_1fr_140px] gap-2 sm:gap-6 py-5 border-t border-[var(--color-rule)] first:border-t-0 items-baseline">
      <p className="font-grotesk text-[0.7rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)]">
        {format(new Date(stamp), "d LLL yyyy")}
        <span className="mx-2">·</span>
        <span className={d.status === "published" ? "text-[var(--color-violet)]" : ""}>{d.status}</span>
        {d.pinnedAt && (
          <>
            <span className="mx-2">·</span>
            <span className="text-gold">pinned</span>
          </>
        )}
      </p>
      <div className="flex gap-3 items-start min-w-0">
        {d.coverImage && (
          <img
            src={d.coverImage}
            alt=""
            className="shrink-0 w-12 h-12 object-cover mt-0.5 hidden sm:block"
          />
        )}
        <div className="min-w-0">
          <button
            type="button"
            onClick={onOpen}
            className="font-display text-[1.4rem] sm:text-[1.5rem] leading-[1.18] tracking-tight hover:text-[var(--color-violet)] transition-colors text-left"
          >
            {d.title}
          </button>
          {d.subtitle && (
            <p className="font-display-italic text-[1rem] text-[var(--color-ink-mute)] mt-1">
              {d.subtitle}
            </p>
          )}
          {d.excerpt && (
            <p className="text-[0.9rem] leading-[1.55] text-[var(--color-ink-soft)] mt-1.5 max-w-[60ch]">
              {d.excerpt}
            </p>
          )}
          {d.tags.length > 0 && (
            <p className="font-grotesk text-[0.66rem] tracking-[0.14em] text-[var(--color-ink-mute)] mt-2">
              {d.tags.map((t) => t.label).join(" · ")}
            </p>
          )}
          {showAuthor && d.authorName && (
            <p className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] mt-1">
              By {d.authorName}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-baseline justify-start sm:justify-end">
        {showEdit ? (
          <>
            <Link to={`/library/${d.slug}`} className="row-action">
              View
            </Link>
            <Link to={`/dashboard/documents/${d.slug}/edit`} className="row-action">
              Edit
            </Link>
          </>
        ) : (
          <Link to={`/library/${d.slug}`} className="row-action">
            Read
          </Link>
        )}
        <span className="font-grotesk text-[0.66rem] text-[var(--color-ink-mute)]">
          {d.attachments.length} resource{d.attachments.length === 1 ? "" : "s"}
        </span>
      </div>
    </li>
  );
}
